import React from 'react';
import styled from 'styled-components';
import LabelWrap from 'components/inputs/LabelWrap';
import CompanyPicker from 'components/CompanyPicker';
import Select from 'components/inputs/Select';
import KeyValueList from 'components/KeyValueList';
import Input from 'components/inputs/Input';
import AutoSizedTextArea from 'components/inputs/AutoSizedTextArea';
import {
  getHoursAndMinutesFromMillisecondsString,
  toInputDateTimeString,
} from 'utils/date-helpers';
import { AssignmentModel, AssignmentRequestReasonEnum } from 'api/model';
import FacilityLockWarning, {
  useToCompanyLock,
} from 'components/AssignmentRequests/FacilityLockWarning';

const Bold = styled.div`
  font-weight: 500;
`;

const ExtraInfoInput = styled(AutoSizedTextArea)`
  resize: vertical;
  min-height: 75px;
  width: 100%;
`;

export type InternalTransportContentProps = {
  selectedFromCompanyId?: number;
  disableFromCompanyPicker?: boolean;
  setSelectedFromCompanyId?: (id?: number) => void;
  selectedToCompanyId?: number;
  setSelectedToCompanyId: (id?: number) => void;
  assingmentRequestReason: AssignmentRequestReasonEnum;
  setAssignmentRequestReason: (reason: AssignmentRequestReasonEnum) => void;
  assignment: AssignmentModel;
  targetAssignmentRequestTime?: Date;
  setTargetAssignmentRequestTime: (d: Date) => void;
  fixedAssignmentTimeInMilliseconds?: number;
  derivedTimeToGetToLogistik: number;
  companiesById: Map<number, { name: string }>;
  assignmentRequestReasonSelectOptions: {
    label: string | React.ReactNode;
    description?: React.ReactNode;
    value: AssignmentRequestReasonEnum;
  }[];
  extraInfo: string;
  setExtraInfo: (v: string) => void;
};

const InternalTransportContent = (props: InternalTransportContentProps) => {
  const {
    selectedFromCompanyId,
    disableFromCompanyPicker,
    setSelectedFromCompanyId,
    selectedToCompanyId,
    setSelectedToCompanyId,
    assingmentRequestReason,
    setAssignmentRequestReason,
    assignment,
    targetAssignmentRequestTime,
    setTargetAssignmentRequestTime,
    fixedAssignmentTimeInMilliseconds,
    derivedTimeToGetToLogistik,
    companiesById,
    assignmentRequestReasonSelectOptions,
    extraInfo,
    setExtraInfo,
  } = props;

  const toCompanyLock = useToCompanyLock(
    selectedToCompanyId,
    targetAssignmentRequestTime
  );

  if (!assignment.estimatedStartTime) {
    return (
      <strong>
        Det går inte att skapa en annonsförfrågan för ett uppdrag som inte har
        en estimerad starttid.
      </strong>
    );
  }
  return (
    <>
      {/* Från */}
      <LabelWrap label={<Bold>Från:</Bold>}>
        <CompanyPicker
          filter={(company) => !!company.isLogisticsCenter}
          disabled={disableFromCompanyPicker}
          onCompanySelected={(company) =>
            setSelectedFromCompanyId?.(company?.companyID ?? undefined)
          }
          selectedCompanyId={selectedFromCompanyId ?? null}
        />
      </LabelWrap>
      {/* Till */}
      <LabelWrap label={<Bold>Till:</Bold>}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CompanyPicker
            filter={(company) => !!company.showInInternalDeliveryOverview}
            onCompanySelected={(company) =>
              setSelectedToCompanyId(company?.companyID ?? undefined)
            }
            selectedCompanyId={selectedToCompanyId ?? null}
          />
          <FacilityLockWarning lock={toCompanyLock.lock} />
        </div>
      </LabelWrap>
      {/* Anledning */}
      <LabelWrap label={<Bold>Anledning:</Bold>}>
        <Select
          value={assingmentRequestReason}
          onSelect={(eve) =>
            setAssignmentRequestReason(
              Number(eve.currentTarget.value) as AssignmentRequestReasonEnum
            )
          }
        >
          {assignmentRequestReasonSelectOptions.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </Select>
      </LabelWrap>
      {targetAssignmentRequestTime && fixedAssignmentTimeInMilliseconds && (
        <>
          <KeyValueList
            colonKey
            rows={[
              {
                key: `Fast tid från Logistik till ${
                  companiesById.get(selectedToCompanyId!)?.name ?? ''
                }`,
                value: (
                  <div>
                    {getHoursAndMinutesFromMillisecondsString(
                      fixedAssignmentTimeInMilliseconds
                    )}
                  </div>
                ),
              },
            ]}
          />
          <LabelWrap label={<Bold>När vill du hämta ut annonsbilen:</Bold>}>
            <Input
              max={toInputDateTimeString(assignment.estimatedStartTime)}
              onChange={(e) => {
                const newValue = new Date(e.currentTarget.value);
                const lastTimeToBeThere = new Date(
                  (assignment.estimatedStartTime!.getTime() || 0) -
                    fixedAssignmentTimeInMilliseconds
                );
                if (newValue.getTime() < lastTimeToBeThere.getTime()) {
                  setTargetAssignmentRequestTime(
                    new Date(e.currentTarget.value)
                  );
                }
              }}
              small
              type="datetime-local"
              value={toInputDateTimeString(targetAssignmentRequestTime)}
            />
          </LabelWrap>
          <KeyValueList
            colonKey
            rows={[
              {
                key: 'Tid att ta sig till Logistik',
                value: (
                  <div>
                    {getHoursAndMinutesFromMillisecondsString(
                      derivedTimeToGetToLogistik
                    )}
                  </div>
                ),
              },
            ]}
          />
          <LabelWrap label={<Bold>Information till annonsgruppen:</Bold>}>
            <ExtraInfoInput
              onChange={(e) => setExtraInfo(e.currentTarget.value)}
              placeholder="Lägg in eventuell information till annonsgruppen här"
              value={extraInfo}
            />
          </LabelWrap>
        </>
      )}
    </>
  );
};

export default InternalTransportContent;
